import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MultiAthletePicker from "@/components/admin/MultiAthletePicker";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import { toast } from "sonner";

type Cause = {
  id: string;
  name: string;
  blurb: string | null;
  raised: number;
  goal: number;
  donate_url: string | null;
  logo_path: string | null;
  image_paths: string[];
  published: boolean;
  sort_order: number;
};

type AthleteRef = { id: string; name: string };

type Editing = (Partial<Cause> & { id?: string; athleteIds: string[] });

const empty = (): Editing => ({
  name: "",
  blurb: "",
  raised: 0,
  goal: 0,
  donate_url: "",
  logo_path: null,
  image_paths: [],
  published: false,
  sort_order: 0,
  athleteIds: [],
});

const AdminCauses = () => {
  const [list, setList] = useState<Cause[]>([]);
  const [athletes, setAthletes] = useState<AthleteRef[]>([]);
  const [links, setLinks] = useState<Record<string, string[]>>({});
  const [editing, setEditing] = useState<Editing | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [c, a, ca] = await Promise.all([
      supabase.from("causes").select("*").order("sort_order").order("created_at", { ascending: false }),
      supabase.from("athletes").select("id,name").order("name"),
      supabase.from("cause_athletes").select("cause_id, athlete_id"),
    ]);
    if (c.error) toast.error(c.error.message);
    if (a.error) toast.error(a.error.message);
    setList((c.data as any) ?? []);
    setAthletes((a.data as any) ?? []);
    const map: Record<string, string[]> = {};
    ((ca.data as any) ?? []).forEach((row: any) => {
      (map[row.cause_id] ||= []).push(row.athlete_id);
    });
    setLinks(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (c: Cause) => {
    setEditing({ ...c, athleteIds: links[c.id] ?? [] });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name) { toast.error("Cause name required"); return; }
    if (editing.athleteIds.length === 0) { toast.error("Select at least one athlete"); return; }
    if ((editing.image_paths ?? []).length > 5) { toast.error("Maximum 5 images"); return; }
    const payload: any = {
      name: editing.name,
      blurb: editing.blurb,
      raised: Number(editing.raised) || 0,
      goal: Number(editing.goal) || 0,
      donate_url: editing.donate_url || null,
      logo_path: editing.logo_path ?? null,
      image_paths: editing.image_paths ?? [],
      published: editing.published,
      sort_order: editing.sort_order ?? 0,
      athlete_id: editing.athleteIds[0], // legacy column
    };
    let causeId = editing.id;
    if (causeId) {
      const { error } = await supabase.from("causes").update(payload).eq("id", causeId);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase.from("causes").insert(payload).select("id").single();
      if (error) return toast.error(error.message);
      causeId = data.id;
    }
    await supabase.from("cause_athletes").delete().eq("cause_id", causeId!);
    const rows = editing.athleteIds.map((aid) => ({ cause_id: causeId!, athlete_id: aid }));
    const { error: linkErr } = await supabase.from("cause_athletes").insert(rows);
    if (linkErr) return toast.error(linkErr.message);
    toast.success("Saved");
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this cause?")) return;
    const { error } = await supabase.from("causes").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const athleteNames = (id: string) =>
    (links[id] ?? []).map((aid) => athletes.find((a) => a.id === aid)?.name).filter(Boolean).join(", ") || "—";

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Causes</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} total</p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          disabled={athletes.length === 0}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
        >
          + New cause
        </button>
      </div>

      {athletes.length === 0 && <p className="text-sm text-muted-foreground mb-4">Create an athlete first to add causes.</p>}

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="grid gap-3">
          {list.map((c) => (
            <div key={c.id} className="bg-card rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-3 mb-2">
                <p className="font-semibold flex-1">{c.name}</p>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${c.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {c.published ? "Live" : "Draft"}
                </span>
                <button onClick={() => startEdit(c)} className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold">Edit</button>
                <button onClick={() => remove(c.id)} className="text-sm font-semibold text-destructive">Delete</button>
              </div>
              <p className="text-xs text-muted-foreground">Backed by {athleteNames(c.id)}</p>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No causes yet.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "New"} cause</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="space-y-4">
              <Field label="Athletes *">
                <MultiAthletePicker
                  athletes={athletes}
                  selected={editing.athleteIds}
                  onChange={(ids) => setEditing({ ...editing, athleteIds: ids })}
                />
              </Field>
              <Field label="Cause name *">
                <input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inp} maxLength={100} />
              </Field>
              <Field label="Blurb">
                <RichTextEditor value={editing.blurb ?? ""} onChange={(v) => setEditing({ ...editing, blurb: v })} />
              </Field>
              <Field label="Donate URL">
                <input
                  type="url"
                  value={editing.donate_url ?? ""}
                  onChange={(e) => setEditing({ ...editing, donate_url: e.target.value })}
                  className={inp}
                  placeholder="https://givebutter.com/your-cause"
                />
              </Field>
              <Field label="Logo">
                <ImageUploader
                  value={editing.logo_path ?? null}
                  onChange={(p) => setEditing({ ...editing, logo_path: p })}
                  prefix="cause-logos"
                />
              </Field>
              <Field label="Images (up to 5)">
                <GalleryUploader
                  value={(editing.image_paths ?? []).slice(0, 5)}
                  onChange={(paths) => setEditing({ ...editing, image_paths: paths.slice(0, 5) })}
                  prefix="cause-gallery"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Raised ($)">
                  <input type="number" value={editing.raised ?? 0} onChange={(e) => setEditing({ ...editing, raised: Number(e.target.value) })} className={inp} />
                </Field>
                <Field label="Goal ($)">
                  <input type="number" value={editing.goal ?? 0} onChange={(e) => setEditing({ ...editing, goal: Number(e.target.value) })} className={inp} />
                </Field>
                <Field label="Sort order">
                  <input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} className={inp} />
                </Field>
                <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-5 h-5 accent-primary" />
                  <span className="font-semibold text-sm">Published</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminCauses;
